import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { materialService } from "../../services/materialService";
import { MagnifyingGlassIcon, FunnelIcon, ShoppingCartIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../../contexts/AuthContext";
import { binService } from "../../services/binService";
import { anonymousBinService } from "../../utils/anonymousBinService";
import Slideshow from "../Common/Slideshow";
import ValidatedBinChangeModal from "../Common/ValidatedBinChangeModal";

const MaterialList = () => {
  const [materials, setMaterials] = useState([]);
  const [filteredMaterials, setFilteredMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [validatedWarningOpen, setValidatedWarningOpen] = useState(false);
  const [pendingMaterial, setPendingMaterial] = useState(null);
  const [confirmingAdd, setConfirmingAdd] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const categories = [
    { value: "all", label: "All Categories" },
    { value: "househole_wastes", label: "Household Wastes" },
    { value: "industrial_waste", label: "Industrial Waste" },
    { value: "raw_meterials", label: "Raw Materials" },
  ];

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        const data = await materialService.getAllMaterials();
        setMaterials(data);
        setFilteredMaterials(data);
      } catch (error) {
        console.error("Error fetching materials:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMaterials();
  }, []);

  useEffect(() => {
    let filtered = materials;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (material) =>
          material.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          material.category.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter(
        (material) => material.category === selectedCategory
      );
    }

    setFilteredMaterials(filtered);
  }, [materials, searchTerm, selectedCategory]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const performQuickAdd = async (material) => {
    await binService.addToBin({
      materialId: material._id,
      quantity: 1,
    });
    if (typeof window !== "undefined" && window.refreshNavbarCounts) {
      window.refreshNavbarCounts();
    }
    navigate("/bin");
  };

  const handleQuickAdd = async (material) => {
    try {
      if (!user) {
        anonymousBinService.addToBin({ material, quantity: 1 });
        if (typeof window !== "undefined" && window.refreshNavbarCounts) {
          window.refreshNavbarCounts();
        }
        navigate("/bin");
        return;
      }

      const res = await binService.getUserBin();
      const raw = res?.data?.bin ?? res?.bin;
      if (raw?.validationStatus) {
        setPendingMaterial(material);
        setValidatedWarningOpen(true);
        return;
      }

      await performQuickAdd(material);
    } catch (e) {
      console.error("Quick add failed:", e);
    }
  };

  const handleConfirmValidatedAdd = async () => {
    if (!pendingMaterial) return;
    setConfirmingAdd(true);
    try {
      await performQuickAdd(pendingMaterial);
      setValidatedWarningOpen(false);
      setPendingMaterial(null);
    } catch (e) {
      console.error("Quick add failed:", e);
    } finally {
      setConfirmingAdd(false);
    }
  };

  return (
    <div className="space-y-6">
      <ValidatedBinChangeModal
        open={validatedWarningOpen}
        onCancel={() => {
          if (confirmingAdd) return;
          setValidatedWarningOpen(false);
          setPendingMaterial(null);
        }}
        onConfirm={handleConfirmValidatedAdd}
        confirming={confirmingAdd}
      />
      <Slideshow />
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Available Materials
        </h1>
        <p className="text-gray-600">
          Browse through our list of materials we buy. Click on any material to
          view details and add to your bin.
        </p>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search materials..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              {categories.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Materials Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {filteredMaterials.map((material) => (
          <div
            key={material._id}
            className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow"
          >
            <div className="aspect-w-16 aspect-h-9 bg-gray-200 rounded-t-lg">
              {material.image ? (
                <img
                  src={material.image}
                  alt={material.name}
                  className="w-full h-48 object-cover rounded-t-lg"
                />
              ) : (
                <div className="w-full h-48 bg-gray-200 rounded-t-lg flex items-center justify-center">
                  <span className="text-gray-500">No Image</span>
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900 truncate">
                  {material.name}
                </h3>
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                  {material.category
                    .replace("_", " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </span>
              </div>
              <p className="text-2xl font-bold text-green-600 mb-4">
                ₦{material.pricePerKg?.toFixed(2) || "0.00"}/kg
              </p>
              <div className="space-y-2">
                <Link
                  to={`/materials/${material._id}`}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-4 rounded-md transition-colors text-center block"
                >
                  Check Details
                </Link>

                <button
                  onClick={() => handleQuickAdd(material)}
                  className="w-full border border-green-600/30 hover:border-green-700 text-green-700 font-medium py-2 px-4 rounded-md transition-colors text-center block flex items-center justify-center gap-2"
                >
                  <ShoppingCartIcon className="h-5 w-5" />
                  Add to Bin
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* No Results */}
      {filteredMaterials.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-600 text-lg">
            {searchTerm || selectedCategory !== "all"
              ? "No materials found matching your criteria."
              : "No materials available at the moment."}
          </p>
          {(searchTerm || selectedCategory !== "all") && (
            <button
              onClick={() => {
                setSearchTerm("");
                setSelectedCategory("all");
              }}
              className="mt-4 text-green-600 hover:text-green-700 font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MaterialList;
